import { memo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { BroadcastFrame, DESIGN_WIDTH, DESIGN_HEIGHT } from './BroadcastFrame.js';
import { ProsceniumFrame } from './ProsceniumFrame.js';
import { ornaments } from './ornaments.js';
import { SceneStack } from '../fx/SceneStack.js';
import { scenesForGame } from '../assets/scenes/index.js';
import { useSequence } from '../fx/sequencer.js';
import { Glyph } from '../ui/Glyph.js';
import cardBack from '../assets/games/bloodmoon-card-back.webp';

/**
 * Stage DealScreen — the role-deal flourish (DEAL phase), DEAL_INTRO sequence.
 *
 * beat title: "The roles are dealt" title fades in. beat cards: a fan of face-down
 * role cards deals out, staggered by cardFanStaggerMs, each pressed with a wax
 * seal (sealStampMs). beat prompt: "Check your phone for your secret role".
 * Ambient-only (no reveal, no identities) — every card is face-down.
 *
 * Two exports: DealScreenContent (inner) + DealScreenDemo (self-framed for
 * CardLab). Back-compat DealScreen keeps the old no-arg signature (StageView
 * renders `<DealScreen />`).
 */

const FAN_COUNT = 5;
const CARD_W = 150;
const CARD_H = 210;

function DealScreenContentImpl() {
  const reduced = useReducedMotion();
  const deal = useSequence('DEAL_INTRO', { enabled: true });
  const staggerS = (deal.params.cardFanStaggerMs ?? 90) / 1000;
  const sealS = (deal.params.sealStampMs ?? 350) / 1000;

  const showTitle = deal.has('title');
  const showCards = deal.has('cards');
  const showPrompt = deal.has('prompt');

  const cx = DESIGN_WIDTH / 2;
  const cy = DESIGN_HEIGHT / 2;

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={showTitle ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
        transition={{ duration: reduced ? 0 : 0.6, ease: [0.7, 0, 0.16, 1] }}
        style={{
          position: 'absolute',
          top: DESIGN_HEIGHT * 0.2,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: 'var(--font-display, serif)',
          fontSize: 'var(--stage-text-title, 96px)',
          background: 'linear-gradient(180deg, var(--gold-6,#f7e3b0), var(--gold-3,#a9762f))',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          color: 'transparent',
          zIndex: 3,
        }}
      >
        The roles are dealt
      </motion.div>

      {/* Card fan */}
      {showCards &&
        Array.from({ length: FAN_COUNT }, (_, i) => {
          const t = i - (FAN_COUNT - 1) / 2; // -2..2
          const angle = t * 10; // degrees
          const dx = t * 130;
          const dy = Math.abs(t) * 18;
          return (
            <motion.div
              key={i}
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 120, rotate: 0 }}
              animate={{ opacity: 1, y: dy, rotate: angle }}
              transition={{ delay: reduced ? 0 : i * staggerS, duration: reduced ? 0 : 0.5, ease: [0.7, 0, 0.16, 1] }}
              style={{
                position: 'absolute',
                left: cx - CARD_W / 2 + dx,
                top: cy - CARD_H / 2,
                width: CARD_W,
                height: CARD_H,
                borderRadius: 'var(--radius-lg, 16px)',
                border: '2px solid var(--gold-3, #a9762f)',
                backgroundImage: `url(${cardBack})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                transformOrigin: 'bottom center',
                zIndex: 4 + i,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {/* Wax seal stamp on each card. */}
              <motion.div
                initial={reduced ? { opacity: 0 } : { scale: 1.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: reduced ? 0 : i * staggerS + 0.25, duration: reduced ? 0 : sealS, ease: [0.7, 0, 0.16, 1] }}
              >
                <Glyph name="seal" tone="blood" size={52} />
              </motion.div>
            </motion.div>
          );
        })}

      {/* Prompt */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={showPrompt ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
        transition={{ duration: reduced ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'absolute',
          bottom: DESIGN_HEIGHT * 0.16,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: 'var(--font-ui, Inter, sans-serif)',
          fontSize: 'var(--stage-text-meta, 24px)',
          letterSpacing: '0.06em',
          color: 'var(--gold-5, #e6b866)',
          zIndex: 6,
        }}
      >
        Check your phone for your secret role
      </motion.div>
    </div>
  );
}

export const DealScreenContent = memo(DealScreenContentImpl);

/** CardLab / standalone — full frame stack (DEAL→NIGHT scene). */
export function DealScreenDemo() {
  const manifest = scenesForGame('bloodmoon');
  const inner = <DealScreenContent />;
  return (
    <BroadcastFrame>
      {manifest ? (
        <SceneStack manifest={manifest} phase="DEAL">
          <ProsceniumFrame cornerSrc={ornaments.filigreeCorner} grainSrc={ornaments.grainTile}>{inner}</ProsceniumFrame>
        </SceneStack>
      ) : (
        <ProsceniumFrame cornerSrc={ornaments.filigreeCorner} grainSrc={ornaments.grainTile}>{inner}</ProsceniumFrame>
      )}
    </BroadcastFrame>
  );
}
